import Decimal from 'decimal.js';

import { api } from './api.js';
import { formatNumber, type NumberingSystem } from './intl.js';

// Client-side mirror of the GL's exchange-rate resolution so forms can preview
// exactly what the server will compute: the newest CUR→BASE rate with
// as_of ≤ the entry date, else the newest BASE→CUR rate inverted, else null
// (the server rejects with currency.rate_not_found).

export interface RateRow {
  fromCurrency: string;
  toCurrency: string;
  rate: string;
  asOf: string;
  /** The real moment the rate was typed (asOf is floored to midnight). Older APIs omit it. */
  createdAt?: string | null;
  /** Who typed it — null for historical rows and automated sources. */
  createdByName?: string | null;
}

export async function loadExchangeRates(): Promise<RateRow[]> {
  try {
    const r = await api().get<{ items: RateRow[] }>('/exchange-rates', { params: { limit: 500 } });
    return r.data.items;
  } catch {
    return [];
  }
}

/** Effective CUR→BASE rate for a date, or null when no rate exists. */
export function effectiveRateFor(rates: RateRow[], currency: string, base: string, date: string): Decimal | null {
  if (currency === base) return new Decimal(1);
  // Server cutoff is MIDNIGHT UTC of the entry date (gl.post does `as_of <= new Date(entryDate)`),
  // so a rate stamped later the same day does NOT apply — mirror that exactly.
  const cutoff = new Date(`${date}T00:00:00.000Z`).getTime();
  const pick = (from: string, to: string): RateRow | null => {
    let best: RateRow | null = null;
    for (const r of rates) {
      if (r.fromCurrency !== from || r.toCurrency !== to) continue;
      if (new Date(r.asOf).getTime() > cutoff) continue;
      if (!best || new Date(r.asOf).getTime() > new Date(best.asOf).getTime()) best = r;
    }
    return best;
  };
  const direct = pick(currency, base);
  if (direct) return new Decimal(direct.rate);
  const inverse = pick(base, currency);
  if (inverse) return new Decimal(1).dividedBy(inverse.rate);
  return null;
}

/** Record a staff-entered rate (stored CUR→BASE, effective from the given date). */
export async function saveExchangeRate(currency: string, base: string, rate: string, date: string): Promise<void> {
  await api().post('/exchange-rates', {
    fromCurrency: currency,
    toCurrency: base,
    rate,
    asOf: new Date(`${date}T00:00:00.000Z`).toISOString(),
  });
}

/** Human rate: trim noise, thousands separators for big rates (e.g. 1,410). */
export function formatRate(r: Decimal, locale: string, numberingSystem?: NumberingSystem): string {
  const n = r.toDecimalPlaces(8);
  // Both branches must use the same digits: the small-rate branch is Decimal's
  // plain ASCII toString(), so the grouped branch goes through formatNumber
  // (Latin by default) instead of inheriting `ar` → Arabic-Indic.
  return n.gte(100)
    ? formatNumber(Number(n.toFixed(2)), { locale, numberingSystem })
    : n.toSignificantDigits(6).toString();
}
